import React, { useMemo, useCallback, useRef, useLayoutEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
// Make sure DailyData is exported from '../../types/dashboard' or import the correct type
// Define DailyData type inline if it does not exist in '../../types/dashboard'
type DailyData = {
  date: string;
  volume: number;
};
import { GREEN } from '../../constants';
import { fmtMonthDay, fmtMDY } from '../../utils/formatters';
import { ChartFrame } from '../charts/ChartFrame';
import { buildCanonicalDailyData } from '../../lib/trades/tradeLevelStats';

interface DailyVolumeWidgetProps {
  dailyData: DailyData[];
  isDark: boolean;
  trades?: any[];
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const rawDate = item?.payload?.date;
  const volume = item?.payload?.volume as number;

  // Support several date shapes: numeric timestamp, YYYY-MM-DD string, or other string.
  let dateLabel = '';
  if (typeof rawDate === 'number') {
    const d = new Date(rawDate);
    dateLabel = `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
  } else if (typeof rawDate === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      dateLabel = fmtMDY(rawDate);
    } else if (!Number.isNaN(Number(rawDate))) {
      const d = new Date(Number(rawDate));
      dateLabel = `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
    } else {
      dateLabel = rawDate;
    }
  } else {
    dateLabel = String(rawDate ?? '');
  }

  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg p-2 text-sm">
      <p className="font-semibold text-zinc-600 dark:text-zinc-300">{dateLabel}</p>
      <p style={{ color: GREEN }}>Volume: {Number(volume).toLocaleString()}</p>
    </div>
  );
};

export const DailyVolumeWidget: React.FC<DailyVolumeWidgetProps> = React.memo(({ dailyData, isDark, trades }) => {
  // Canonical source of truth: when the authoritative trade records are provided,
  // the daily volume series is rebuilt from the canonical closed trades (the same
  // filter + daily aggregation every other widget uses). This guarantees the chart
  // is never empty merely because the dashboard-range dailyData is empty.
  const canonicalDailyData = useMemo(
    () => (Array.isArray(trades) && trades.length > 0 ? buildCanonicalDailyData(trades, 366) : null),
    [trades],
  );
  const source = canonicalDailyData ?? dailyData ?? [];
  const hasData = source.length > 0;

  const gridColor = isDark ? "#404040" : "#e5e7eb";
  const textColor = isDark ? "#a1a1aa" : "#6b7280";

  // Show only the most recent 7 days
  const chartData = useMemo(() => source.slice(-7), [source]);

  // Ensure every data category on the X axis has a tick/label
  const xTicks = useMemo(() => chartData.map(d => d.date), [chartData]);

  // Increase top margin to 8 to match other widgets and avoid top label crowding
  // Set left to 0 to ensure labels are never clipped by a negative margin
  const margin = useMemo(() => ({ top: 8, right: 10, left: 0, bottom: 8 }), []);

  // Measure header left so we can align Y-axis labels with the header's left edge
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [yLabelX, setYLabelX] = useState<number | null>(null);

  useLayoutEffect(() => {
    const compute = () => {
      const el = containerRef.current;
      if (!el) return;
      const widget = el.closest('.widget-card') as HTMLElement | null;
      const header = widget?.querySelector('h3, h2') as HTMLElement | null;
      const chartRect = el.getBoundingClientRect();
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const offset = Math.round(headerRect.left - chartRect.left);
        const inset = 0;
        setYLabelX(Math.max(0, offset + inset));
      } else {
        setYLabelX(8);
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  const xTickStyle = useMemo(() => ({ fontSize: 12, fill: textColor }), [textColor]);

  // Reserve Y-axis width based on formatted label widths so labels don't clip and the axis aligns consistently
  const yAxisWidth = useMemo(() => {
    try {
      const values = (chartData || []).map(d => d.volume ?? 0);
      if (!values.length) return 48;
      const labels = values.map(v => Number(v).toLocaleString());
      labels.push((0).toLocaleString());
      let maxW = 0;
      const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
      const ctx = canvas ? canvas.getContext('2d') : null;
      if (ctx) {
        ctx.font = '12px sans-serif';
        for (const l of labels) {
          const w = ctx.measureText(l).width;
          if (w > maxW) maxW = w;
        }
      } else {
        for (const l of labels) {
          const w = l.length * 6;
          if (w > maxW) maxW = w;
        }
      }
      const GAP = 4; // 4px space between axis line and label right edge
      const reserved = Math.ceil(maxW) + GAP;
      return Math.max(reserved, 44);
    } catch {
      return 48;
    }
  }, [chartData]);
  
  const xTickFormatter = useCallback((value: string) => fmtMonthDay(value), []);
  const tooltipContent = useMemo(() => (<CustomTooltip />), []);

  // Bar-sized hover highlight (visible even when value is 0)
  const ActiveBar = (props: any) => {
    const { x, y, width, height } = props;
    if (width <= 0) return null;
    const minH = 4; // ensure visible highlight on zero values
    const baseH = height || 0;
    const extra = Math.max(minH - baseH, 0);
    const adjY = (y ?? 0) - extra;
    const adjH = baseH + extra;
    return (
      <rect x={x} y={adjY} width={width} height={adjH} fill="rgba(156, 163, 175, 0.3)" rx={2} ry={2} />
    );
  };

  // Adjusted Y-axis label alignment using measured header-left offset
  const renderYTick = useCallback(({ x, y, payload }: any) => {
    const raw = payload?.value;
    const axisWidth = yAxisWidth;
    const leftMargin = (margin && (margin as any).left) ? (margin as any).left : 0;
    // Desired gap between the Y-axis and the label text
    const gap = 4;
    // Prefer Recharts-provided axis X for exact alignment; fallback to header-based estimate
    const axisX = (typeof x === 'number') ? x : (typeof yLabelX === 'number' ? (yLabelX + axisWidth + leftMargin) : x);
    const measured = axisX - gap;
    // Pixel-align Y to avoid subpixel blur and off-by-1 illusion at the top tick
    let yAligned = y;
    try {
      const dpr = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1;
      yAligned = Math.round(y * dpr) / dpr;
    } catch {}
    // Format numbers with thousands separators (e.g., 1,234). If value is a numeric string, coerce to number first.
    let label: string;
    const num = (typeof raw === 'number') ? raw : (raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null);
    if (num !== null) {
      label = num.toLocaleString();
    } else {
      label = raw == null ? '' : String(raw);
    }
    return (
      <text x={measured} y={yAligned} fill={isDark ? '#9ca3af' : '#374151'} fontSize={12} textAnchor="end" dominantBaseline="middle">
        {label}
      </text>
    );
  }, [isDark, yLabelX, margin, yAxisWidth]);

  return (
  <div className="h-full flex flex-col" ref={containerRef}>
    {!hasData ? (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <div>
          <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">No volume data yet.</div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">Import trades to see this widget.</div>
        </div>
      </div>
    ) : (
  <div
    data-testid="daily-volume-chart-container"
    className="-ml-5 w-[calc(100%+20px)] max-w-none flex-1 overflow-visible"
  >
        <ChartFrame>
          {({ width, height }: { width: number; height: number }) => (
            <BarChart width={width} height={height} data={chartData} margin={margin}>
              <CartesianGrid vertical={false} stroke={gridColor} />
              <XAxis dataKey="date" ticks={xTicks} interval={0} allowDuplicatedCategory={true} height={36} tickMargin={12} tick={xTickStyle as any} tickFormatter={xTickFormatter} />
              <YAxis width={yAxisWidth} tick={renderYTick} tickMargin={0} axisLine={true} tickLine={true} />
              <Tooltip cursor={false} content={tooltipContent as any} />
              <Bar dataKey="volume" fill={GREEN} activeBar={<ActiveBar />} />
            </BarChart>
          )}
        </ChartFrame>
      </div>
      )}
      {/* Year label removed */}
    </div>
  );
}, (prev, next) => {
  if (prev.isDark !== next.isDark) return false;
  const a = prev.dailyData, b = next.dailyData;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const la = a[a.length - 1]?.date;
  const lb = b[b.length - 1]?.date;
  return la === lb;
});
